import { useQuery } from "@tanstack/react-query";
import {
  LuLayoutDashboard,
  LuUsers,
  LuMail,
  LuGlobe,
  LuKey,
  LuMessageSquare,
  LuDollarSign,
  LuHandshake,
} from "react-icons/lu";
import type { ComponentType } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page-header";
import { SkeletonStatGrid, SkeletonCard } from "@/components/ui/skeleton";

export function OverviewPage() {
  const metrics = useQuery({
    queryKey: ["console", "admin", "metrics"],
    queryFn: () => api.adminMetrics(),
  });
  const summary = useQuery({
    queryKey: ["console", "overview", "summary"],
    queryFn: () => api.overview.summary(),
  });

  return (
    <>
      <PageHeader
        title="Overview"
        icon={LuLayoutDashboard}
        description="High-level health of the destroy.network product."
      />
      <div className="p-8 space-y-6">
        {(metrics.isLoading || summary.isLoading) && (
          <div className="space-y-6">
            <SkeletonStatGrid count={8} />
            <SkeletonCard rows={4} />
          </div>
        )}

        {metrics.data && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat
              icon={LuUsers}
              label="Total users"
              value={metrics.data.users.total.toLocaleString()}
              footer={`${metrics.data.users.active.toLocaleString()} active`}
              tone="violet"
            />
            <Stat
              icon={LuMail}
              label="Inboxes (lifetime)"
              value={metrics.data.inboxes.total.toLocaleString()}
              footer={`${metrics.data.inboxes.current.toLocaleString()} current · ${metrics.data.inboxes.active.toLocaleString()} active`}
              tone="cyan"
            />
            <Stat
              icon={LuMessageSquare}
              label="Messages (lifetime)"
              value={metrics.data.messages.total.toLocaleString()}
              footer={`${metrics.data.messages.current.toLocaleString()} current`}
              tone="emerald"
            />
            <Stat
              icon={LuGlobe}
              label="Custom domains"
              value={metrics.data.domains.total.toLocaleString()}
              footer={`${metrics.data.domains.verified.toLocaleString()} verified`}
              tone="amber"
            />
            <Stat
              icon={LuKey}
              label="API keys"
              value={metrics.data.apiKeys.total.toLocaleString()}
              tone="violet"
            />
            {summary.data && (
              <>
                <Stat
                  icon={LuDollarSign}
                  label="MRR"
                  value={`$${summary.data.revenue.mrr.toLocaleString()}`}
                  tone="emerald"
                />
                <Stat
                  icon={LuHandshake}
                  label="Sponsor revenue"
                  value={`$${summary.data.revenue.sponsorsTotal.toLocaleString()}`}
                  tone="amber"
                />
                <Stat
                  icon={LuMail}
                  label="Inboxes today"
                  value={summary.data.inboxes.createdToday.toLocaleString()}
                  tone="cyan"
                />
              </>
            )}
          </div>
        )}

        {/* Tier breakdown */}
        {metrics.data && Object.keys(metrics.data.users.byTier).length > 0 && (
          <Card>
            <div className="px-5 py-4 border-b border-[var(--color-border)] flex items-center gap-2">
              <LuUsers className="w-4 h-4 text-[var(--color-text-subtle)]" />
              <h2 className="text-sm font-semibold text-[var(--color-text)]">
                Users by tier
              </h2>
            </div>
            <div className="p-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {Object.entries(metrics.data.users.byTier).map(([tier, count]) => (
                <div
                  key={tier}
                  className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-4"
                >
                  <p className="text-[10px] uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
                    {tier}
                  </p>
                  <p className="text-xl font-semibold text-[var(--color-text)] mt-1">
                    {count.toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Recent users */}
        {metrics.data && metrics.data.recentUsers.length > 0 && (
          <Card>
            <div className="px-5 py-4 border-b border-[var(--color-border)] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <LuUsers className="w-4 h-4 text-[var(--color-text-subtle)]" />
                <h2 className="text-sm font-semibold text-[var(--color-text)]">
                  Recent signups
                </h2>
              </div>
              <span className="text-xs text-[var(--color-text-subtle)]">
                Last {metrics.data.recentUsers.length}
              </span>
            </div>
            <div className="divide-y divide-[var(--color-border)]">
              {metrics.data.recentUsers.map((u) => (
                <div
                  key={u.id}
                  className="px-5 py-3 flex items-center justify-between gap-4 hover:bg-[var(--color-elevated)]/40 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[var(--color-accent)] to-[var(--color-glow)] flex items-center justify-center text-white text-[10px] font-semibold shrink-0">
                      {u.email.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm text-[var(--color-text)] truncate">
                      {u.email}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs shrink-0">
                    <TierPill tier={u.tier} />
                    <span className="text-[var(--color-text-subtle)] font-mono tabular-nums">
                      {new Date(u.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </>
  );
}

const TONE: Record<string, { bg: string; border: string; text: string }> = {
  violet: {
    bg: "bg-[rgba(139,92,246,0.1)]",
    border: "border-[rgba(139,92,246,0.25)]",
    text: "text-[var(--color-accent-light)]",
  },
  cyan: {
    bg: "bg-[rgba(65,209,255,0.08)]",
    border: "border-[rgba(65,209,255,0.25)]",
    text: "text-[#41d1ff]",
  },
  emerald: {
    bg: "bg-[rgba(66,184,131,0.08)]",
    border: "border-[rgba(66,184,131,0.25)]",
    text: "text-[#6ee7b7]",
  },
  amber: {
    bg: "bg-[rgba(250,176,5,0.08)]",
    border: "border-[rgba(250,176,5,0.25)]",
    text: "text-[#fcd34d]",
  },
};

function Stat({
  icon: Icon,
  label,
  value,
  footer,
  tone = "violet",
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string;
  footer?: string;
  tone?: keyof typeof TONE;
}) {
  const t = TONE[tone];
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-5 hover:border-[var(--color-border-hover)] transition-colors">
      <div className="flex items-start justify-between mb-3">
        <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--color-text-subtle)]">
          {label}
        </p>
        <div
          className={`w-7 h-7 rounded-md flex items-center justify-center border ${t.bg} ${t.border}`}
        >
          <Icon className={`w-3.5 h-3.5 ${t.text}`} />
        </div>
      </div>
      <p className="text-2xl font-semibold text-[var(--color-text)] tracking-tight tabular-nums">
        {value}
      </p>
      {footer && (
        <p className="text-[11px] text-[var(--color-text-subtle)] mt-1.5">{footer}</p>
      )}
    </div>
  );
}

const TIER_PILL: Record<string, string> = {
  free: "bg-[var(--color-elevated)] text-[var(--color-text-muted)] border-[var(--color-border)]",
  pro: "bg-[rgba(139,92,246,0.12)] text-[var(--color-accent-light)] border-[rgba(139,92,246,0.3)]",
  business: "bg-[rgba(65,209,255,0.1)] text-[#41d1ff] border-[rgba(65,209,255,0.3)]",
  enterprise: "bg-[rgba(189,52,254,0.12)] text-[#d8b4fe] border-[rgba(189,52,254,0.3)]",
};

function TierPill({ tier }: { tier: string }) {
  const cls = TIER_PILL[tier] ?? TIER_PILL.free;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-md border ${cls}`}
    >
      {tier}
    </span>
  );
}
